import { useEffect, useRef, useMemo, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import copy from 'copy-to-clipboard';
import {
  IconLoader,
  Table,
  useQueryState,
  useScroll,
  useFilters,
  usePrevious,
  useGetList,
  PermissionEnum,
  SchemaFilterType,
  IconArrowDiagonal,
  IconClock,
  formatDate,
  IconSelected,
  IconCopyTwo,
} from '@ui/src';
import { useAppSelector, useAppDispatch } from '@store';
import { clearIntegrationLogsState } from './store/slice';
import { IntegrationLog } from './types';
import {
  integrationLogsColumns,
  integrationLogsColumnsLabels,
  integrationLogsSchema,
  integrationLogsFiltersSchema,
} from './variables/list';
import {
  useLazyGetIntegrationsLogsQuery,
  useLazyGetPspPartnerListQuery,
} from './store/service';

import cn from './integration-logs.module.css';

const formatIntegrationLogs = (
  list: IntegrationLog[],
  onCopy: (text: string) => void,
  copyIsSuccess?: boolean,
): IntegrationLog[] => {
  const nextList = list?.map((item) => {
    const result = { ...item };

    let statusCodeClassName = '';
    let responseTimeClassName = '';
    let type;

    if (item?.statusCode) {
      if (item?.statusCode?.toString()?.charAt(0) === '2') {
        statusCodeClassName = cn.successStatus;
      } else if (item?.statusCode?.toString()?.charAt(0) === '3') {
        statusCodeClassName = cn.redirectStatus;
      } else if (item?.statusCode?.toString()?.charAt(0) === '4') {
        statusCodeClassName = cn.errorStatusCode;
      } else if (item?.statusCode?.toString()?.charAt(0) === '5') {
        statusCodeClassName = cn.internalErrorStatusCode;
      }
    }

    if (item?.requestTime && item?.responseTime) {
      const difference =
        new Date(item?.responseTime).getTime() -
        new Date(item.requestTime).getTime();

      if (difference >= 1500 && difference <= 2500) {
        responseTimeClassName = cn.yellowIcon;
      } else if (difference > 2500) {
        responseTimeClassName = cn.redIcon;
      }
    } else if (!item.responseTime) {
      responseTimeClassName = cn.redIcon;
    }

    if (item?.types) {
      type = (
        <div className={cn.logType}>
          <IconArrowDiagonal
            width={10}
            height={10}
            className={item?.types === 'WEBHOOK' ? cn.webhookIcon : ''}
          />
          {`${item?.types?.charAt(0)}${item?.types?.slice(1).toLowerCase()}`}
        </div>
      );
    } else {
      type = '-';
    }

    const render: Record<string, React.JSX.Element> = {
      types: <div>{type}</div>,
      pspPartnerIds: (
        <div className={cn.pspPartnerName}>
          {item?.pspPartner?.companyName || '-'}
        </div>
      ),
      transactionIds: (
        <div className={`${cn.pspPartnerName} ${cn.transactionId}`}>
          {item?.transactionId || '-'}
        </div>
      ),
      url: (
        <div className={cn.urlEventTypeWrapper}>
          {item?.eventType && (
            <div className={cn.listEventTypeWrapper}>
              <div className={cn.eventType}>{item.eventType}</div>
            </div>
          )}
          {item?.url && (
            <div className={cn.logUrl}>
              <span>{item.url}</span>
            </div>
          )}
          {!item?.eventType && !item?.url && '-'}
        </div>
      ),
      requestTime: (
        <div>
          {item?.requestTime ? formatDate(item.requestTime, true, true) : '-'}
        </div>
      ),
      responseTime: (
        <div className={cn.logsResponseTime}>
          {item?.responseTime ? formatDate(item.responseTime, true, true) : '-'}
          <div className={cn.logsResponseTimeIcon}>
            <div className={cn.logsResponseLatency}>{`Latency: ${
              item?.requestTime && item?.responseTime
                ? new Date(item?.responseTime).getTime() -
                  new Date(item.requestTime).getTime()
                : '-'
            } ms`}</div>
            <IconClock
              width={14}
              height={14}
              className={responseTimeClassName}
            />
          </div>
        </div>
      ),
      statusCode: (
        <div>
          {item?.statusCode ? (
            <div className={`${cn.logStatusCode} ${statusCodeClassName}`}>
              {item.statusCode}
            </div>
          ) : (
            '-'
          )}
        </div>
      ),
      responseBody: (
        <div className={cn.logsResponseBodyWrapper}>
          <div className={cn.logsResponseBody}>
            <span>{item?.responseBody || '-'}</span>
          </div>
          <button
            type="button"
            className={cn.copyListButton}
            onClick={() =>
              onCopy(
                item?.responseBody
                  ? JSON.stringify(
                      item?.responseBody.includes('{') &&
                        item?.responseBody.charAt(0) === '{'
                        ? JSON.parse(item?.responseBody)
                        : item?.responseBody,
                      null,
                      2,
                    )
                  : '-',
              )
            }
          >
            {copyIsSuccess ? (
              <IconSelected />
            ) : (
              <IconCopyTwo width={16} height={16} />
            )}
          </button>
        </div>
      ),
    };

    result.render = render;

    return result;
  });

  return nextList;
};

const IntegrationLogsList = () => {
  const scrollRef = useRef<HTMLDivElement | null>(null);

  const [lastFiveMinActive, setLastFiveMinActive] = useState(false);
  const [lastFiveteenMinActive, setLastFiveteenMinActive] = useState(false);
  const [copyIsSuccess, setCopyIsSuccess] = useState(false);

  const {
    configurationDictionaries: { activePermissions },
    integrationLogs: { logs, pagination, pspPartners, errorsList },
  } = useAppSelector((state) => state);

  const dispatch = useAppDispatch();

  const { filters, sort, onSortChange, onFilterChange } = useQueryState(
    integrationLogsSchema,
  );

  const prevFilters = usePrevious(filters);

  const { search } = useLocation();

  const onCopy = (text: string) => {
    setCopyIsSuccess(true);

    copy(text);

    setTimeout(() => {
      setCopyIsSuccess(false);
    }, 2000);
  };

  const formattedData = useMemo(() => {
    const data = formatIntegrationLogs(
      logs as IntegrationLog[],
      onCopy,
      copyIsSuccess,
    );

    return data;
  }, [logs, sort, filters, copyIsSuccess, onCopy]);

  const [getList, { isFetching, isLoading }] =
    useLazyGetIntegrationsLogsQuery();

  const [getPspPartners] = useLazyGetPspPartnerListQuery();

  const { getListAction, onFilter, setPage, errorList } = useGetList({
    getList,
    onFilterChange,
    onSortChange,
    schema: integrationLogsSchema,
  });

  const { filtersComponent, setValues, handleSubmit } = useFilters({
    filtersSchema: integrationLogsFiltersSchema,
    filters,
    onFilterChange: onFilter,
  });

  useScroll({
    element: scrollRef,
    requestInProgress: isFetching,
  });

  useEffect(() => {
    if (
      activePermissions?.includes(PermissionEnum.PSP_INTEGRATION_LOGS_LIST) &&
      filters &&
      Object.keys(filters)?.length &&
      search &&
      !logs
    ) {
      const nextFilters = { ...filters };

      const responeTimeStorage = localStorage.getItem('logsResponseTime');

      if (responeTimeStorage) {
        const [from, to] = JSON.parse(responeTimeStorage);

        const difference =
          (new Date(to).getTime() - new Date(from).getTime()) / (1000 * 60);

        if (difference === 5) {
          setLastFiveMinActive(true);
        } else if (difference === 15) {
          setLastFiveteenMinActive(true);
        }

        nextFilters.responseTime = {
          type: SchemaFilterType.DATE_RANGE,
          value: [from, to],
        };
      }

      getListAction({
        filters: nextFilters,
        withoutTimeZone: true,
      });
    }
  }, [activePermissions, logs, filters, search]);

  useEffect(() => {
    if (
      activePermissions?.includes(PermissionEnum.PSP_INTEGRATION_LOGS_LIST) &&
      !search &&
      !logs
    ) {
      getListAction({
        filters,
        withoutTimeZone: true,
      });
    }
  }, [activePermissions, logs, search]);

  useEffect(() => {
    if (prevFilters?.responseTime && !filters.responseTime) {
      setLastFiveMinActive(false);
      setLastFiveteenMinActive(false);
      localStorage.removeItem('responseTime');
    }
  }, [prevFilters, filters]);

  useEffect(() => {
    dispatch(clearIntegrationLogsState());
  }, []);

  useEffect(() => {
    if (pspPartners) {
      setValues('pspPartnerIds', pspPartners);
    }

    if (!pspPartners) {
      getPspPartners();
    }
  }, [pspPartners]);

  useEffect(() => {
    let title = 'Integration Logs';

    const companyName = localStorage.getItem('companyName');

    if (companyName) {
      title = `${title} | ${companyName}`;
    }

    document.title = title;
  }, []);

  let content: React.JSX.Element;

  if (isLoading) {
    content = (
      <div className={cn.integrationLogsLoader}>
        <IconLoader />
      </div>
    );
  } else {
    content = (
      <div className={cn.integrationLogsTableWrapper}>
        <div className={cn.integrationLogsTable}>
          <Table
            title="Integration Logs"
            columns={integrationLogsColumns}
            data={formattedData || []}
            onSort={() => {}}
            filters={filters}
            sort={{}}
            columnsLabels={integrationLogsColumnsLabels}
            onFilterChange={(filterValues) => {
              onFilter(filterValues, true);
            }}
            error={errorsList || errorList}
            filtersComponent={filtersComponent}
            handleSubmit={handleSubmit}
            isLoading={isFetching}
            pagination={pagination}
            take="30"
            setPage={(data) => {
              setPage({
                ...data,
                filters: {
                  ...filters,
                },
              });
            }}
            topContent={
              <div className={cn.filtersButtons}>
                <button
                  type="button"
                  className={`${cn.filterButton} ${
                    filters?.types?.value?.includes('WEBHOOK') ? cn.active : ''
                  }`}
                  onClick={() => {
                    const nextTypes = [];

                    if (filters?.types?.value?.includes('REQUEST')) {
                      nextTypes.push('REQUEST');
                    }

                    if (!filters?.types?.value?.includes('WEBHOOK')) {
                      nextTypes.push('WEBHOOK');
                    }

                    onFilter(
                      {
                        ...filters,
                        types: {
                          value: nextTypes,
                          type: SchemaFilterType.STRING,
                        },
                      },
                      true,
                    );
                  }}
                >
                  Webhooks
                </button>
                <button
                  type="button"
                  className={`${cn.filterButton} ${
                    filters?.types?.value?.includes('REQUEST') ? cn.active : ''
                  }`}
                  onClick={() => {
                    const nextTypes = [];

                    if (filters?.types?.value?.includes('WEBHOOK')) {
                      nextTypes.push('WEBHOOK');
                    }

                    if (!filters?.types?.value?.includes('REQUEST')) {
                      nextTypes.push('REQUEST');
                    }

                    onFilter(
                      {
                        ...filters,
                        types: {
                          value: nextTypes,
                          type: SchemaFilterType.STRING,
                        },
                      },
                      true,
                    );
                  }}
                >
                  Requests
                </button>
                <button
                  type="button"
                  className={`${cn.filterButton} ${
                    lastFiveMinActive ? cn.active : ''
                  }`}
                  onClick={() => {
                    const nextFilters = { ...filters };

                    const dateTime = new Date().getTime();

                    const lastFiveMinTime = dateTime - 1000 * 60 * 5;

                    if (filters?.responseTime && lastFiveMinActive) {
                      delete nextFilters.responseTime;
                      setLastFiveMinActive(false);

                      localStorage.removeItem('logsResponseTime');
                    } else {
                      nextFilters.responseTime = {
                        type: SchemaFilterType.DATE_RANGE,
                        value: [
                          new Date(lastFiveMinTime).toISOString(),
                          new Date(dateTime).toISOString(),
                        ],
                      };

                      localStorage.setItem(
                        'logsResponseTime',
                        JSON.stringify([
                          new Date(lastFiveMinTime).toISOString(),
                          new Date(dateTime).toISOString(),
                        ]),
                      );

                      setLastFiveMinActive(true);
                      setLastFiveteenMinActive(false);
                    }

                    onFilter(nextFilters, true);
                  }}
                >
                  Last 5 min
                </button>
                <button
                  type="button"
                  className={`${cn.filterButton} ${
                    lastFiveteenMinActive ? cn.active : ''
                  }`}
                  onClick={() => {
                    const nextFilters = { ...filters };

                    const dateTime = new Date().getTime();

                    const lastFiveteenMinTime = dateTime - 1000 * 60 * 15;

                    if (filters?.responseTime && lastFiveteenMinActive) {
                      delete nextFilters.responseTime;
                      setLastFiveteenMinActive(false);
                      localStorage.removeItem('logsResponseTime');
                    } else {
                      nextFilters.responseTime = {
                        type: SchemaFilterType.DATE_RANGE,
                        value: [
                          new Date(lastFiveteenMinTime).toISOString(),
                          new Date(dateTime).toISOString(),
                        ],
                      };

                      localStorage.setItem(
                        'logsResponseTime',
                        JSON.stringify([
                          new Date(lastFiveteenMinTime).toISOString(),
                          new Date(dateTime).toISOString(),
                        ]),
                      );

                      setLastFiveteenMinActive(true);
                      setLastFiveMinActive(false);
                    }

                    onFilter(nextFilters, true);
                  }}
                >
                  Last 15 min
                </button>
              </div>
            }
            clickable
            withScroll
            withVerticalScroll
          />
        </div>
      </div>
    );
  }

  return (
    <div className={cn.integrationLogsBottom} ref={scrollRef}>
      {content}
      <Outlet />
    </div>
  );
};

export default IntegrationLogsList;
